/**
 * 証拠金レポートチャートの系列定義。
 *
 * 色・線種（dash）・線幅・ドット形状はここが唯一の定義場所（single source of truth）。
 * MarginReportChart.tsx（グラフ描画）・MarginReportLegend.tsx（凡例）はどちらも
 * この定義を参照してスタイルを決定する。ハードコードしないこと。
 *
 * 実測値の根拠: プロトタイプ DOM 実測（2026-09-15）。
 */

/** 凡例スウォッチ・グラフの dot に使う形状。'none' はドットを描画しない。 */
export type MarginReportSeriesDotShape =
  | 'circle'
  | 'square'
  | 'diamond'
  | 'hexagon'
  | 'triangle'
  | 'none'

/**
 * Area 系列の塗り指定。
 *
 * - 'none': 塗りなし（Line 系列や、塗りを持たない系列）。
 * - 'solid': 単色塗り。opacity 未指定時は recharts の Area の既定値（0.6）が使われる。
 * - 'gradient': グラデーション塗り。**グラデーション自体の定義（<linearGradient>）は
 *   series.ts では持たず、Chart 側（MarginReportChart.tsx）の <defs> に置く**。
 *   id は `${key}Gradient`（例: 'unrealizedAmountGradient'）という命名規約に従うこと。
 *   凡例スウォッチはどのみち塗りを描画しないため、'gradient' でも見た目上の影響はない。
 */
export type MarginReportSeriesFill =
  | { kind: 'none' }
  | { kind: 'solid'; color: string; opacity?: number }
  | { kind: 'gradient' }

export type MarginReportSeriesStyle = {
  stroke: string
  /** undefined の場合は実線 */
  strokeDasharray?: string
  strokeWidth: number
  fill: MarginReportSeriesFill
  dot: MarginReportSeriesDotShape
  /**
   * 凡例スウォッチの描画形状。未指定時は 'line'（既存の線＋ドット）。
   * 'band' は破線枠＋薄塗りの角丸帯（プロトタイプ実測: 2026-09-18。deposits の
   * hybrid スタイルのみが使う）。
   */
  legendSwatch?: 'line' | 'band'
}

export type MarginReportSeriesKey =
  | 'totalCreditAmount'
  | 'deposits'
  | 'requiredMargin'
  | 'initialMargin'
  | 'unrealizedAmount'
  | 'realizedAmount'
  | 'materializedUnsettledAmount'
  | 'maxTradableAmount'
  | 'marginCallTiming'

/** 凡例での操作系 UI 種別。 */
export type MarginReportSeriesControl = 'checkbox' | 'switch'

// component.json の `component:features.margin_report.legend.*` /
// `legend_description.*` に対応するキー（接頭辞は含まない）。MarginReportSeriesDef の
// labelKey / descriptionKey をここから導出される template literal 型にすることで、
// def を `MarginReportSeriesDef` 型の変数・関数引数・prop として受け渡した後（Pick や
// コンポーネント境界を跨いだ後）も widen された `string` にならず、portal 等の
// i18next CustomTypeOptions による `t(labelKey)` の厳密なキー型チェックを通る
// （`marginReportSeriesByKey.deposits` のような const オブジェクトの個別プロパティ参照に
// 限定されない）。
type MarginReportSeriesLabelSuffix =
  | 'total_credit_amount'
  | 'deposits'
  | 'required_margin'
  | 'initial_margin'
  | 'unrealized_amount'
  | 'realized_amount'
  | 'mtm'
  | 'max_tradable_amount'
  | 'margin_call_timing'

// mtm（未精算代金合計）のみ legend_description の対応エントリが無い。
type MarginReportSeriesDescriptionSuffix = Exclude<
  MarginReportSeriesLabelSuffix,
  'mtm'
>

export type MarginReportSeriesDef = {
  key: MarginReportSeriesKey
  labelKey: `component:features.margin_report.legend.${MarginReportSeriesLabelSuffix}`
  /** 説明ツールチップの i18next key。存在する系列のみ */
  descriptionKey?: `component:features.margin_report.legend_description.${MarginReportSeriesDescriptionSuffix}`
  /** ハイブリッドスキーム（履行保証あり）でのみ表示する系列か */
  hybridOnly: boolean
  /** 凡例での操作系 UI。'switch' は marginCallTiming のみ。 */
  control: MarginReportSeriesControl
  /**
   * チャート描画（重なり）順。小さいほど奥、大きいほど手前に描かれる。
   * 塗り（Area）を持つ系列を奥に、既定 ON の系列を手前にする意図。
   * 凡例の表示順（{@link MARGIN_REPORT_SERIES_KEY_ORDER}）とは独立した値で、
   * 互いに連動させないこと。
   */
  drawOrder: number
  /**
   * スタイルがハイブリッド/預託金のみで変わらない系列は単一の Style を、
   * 変わる系列（deposits）は { hybrid, depositOnly } を持つ。
   */
  style: MarginReportSeriesStyle | HybridVariantStyle
  /**
   * 初期表示 ON/OFF。ハイブリッド専用系列の depositOnly 側は非表示のため常に false。
   * hybrid 側は変更管理表 6.（初期表示3要素: totalCreditAmount / requiredMargin /
   * maxTradableAmount + marginCallTiming）どおり。depositOnly 側は本番（develop の
   * MarginReportChart.tsx useState 初期値）を踏襲する（2026-09-18 プロダクト判断）。
   */
  defaultVisible: {
    hybrid: boolean
    depositOnly: boolean
  }
}

type HybridVariantStyle = {
  hybrid: MarginReportSeriesStyle
  depositOnly: MarginReportSeriesStyle
}

function isHybridVariantStyle(
  style: MarginReportSeriesStyle | HybridVariantStyle,
): style is HybridVariantStyle {
  return 'hybrid' in style && 'depositOnly' in style
}

/** 系列定義から、現在のモードに応じたスタイルを解決する */
export function resolveMarginReportSeriesStyle(
  def: MarginReportSeriesDef,
  isHybrid: boolean,
): MarginReportSeriesStyle {
  if (!isHybridVariantStyle(def.style)) {
    return def.style
  }
  return isHybrid ? def.style.hybrid : def.style.depositOnly
}

// switch の網羅性チェック用。新しい MarginReportSeriesDotShape が追加された際に
// Chart 側（dot レンダラー）・Legend 側（凡例スウォッチ）どちらの case 追加漏れも
// ここでコンパイルエラーになる。
export function assertUnreachableDotShape(shape: never): never {
  throw new Error(`Unhandled MarginReportSeriesDotShape: ${String(shape)}`)
}

/**
 * 現在のモード（isHybrid）で表示対象になりうる系列かどうか。
 * hybridOnly な系列はハイブリッドスキームでのみ表示対象になる。
 * Chart（drawableSeries）・Legend（visibleSeries）の両方が使うフィルタ条件。
 */
export function isSeriesAvailable(
  def: Pick<MarginReportSeriesDef, 'hybridOnly'>,
  isHybrid: boolean,
): boolean {
  return isHybrid || !def.hybridOnly
}

const LEGEND_KEY = 'component:features.margin_report.legend'
const LEGEND_DESCRIPTION_KEY =
  'component:features.margin_report.legend_description'

const NONE_FILL: MarginReportSeriesFill = { kind: 'none' }
const GRADIENT_FILL: MarginReportSeriesFill = { kind: 'gradient' }

/**
 * 系列定義の正本（single source of truth）。
 *
 * `satisfies` により 9 キー（{@link MarginReportSeriesKey}）の網羅がコンパイル時に
 * 保証される。`labelKey` / `descriptionKey` 自体が {@link MarginReportSeriesDef} の時点で
 * template literal 型（widen された `string` ではない）のため、
 * `marginReportSeriesByKey.deposits` のような個別プロパティ参照に限らず、
 * `MarginReportSeriesDef` 型で受け渡された変数・関数引数・prop（Pick やコンポーネント境界を
 * 跨いだ後）でも literal 型のまま保持され、i18next の CustomTypeOptions によりキーが
 * literal union で型付けされているアプリ（portal 等）で `t(labelKey)` が型エラーにならない。
 *
 * Chart / Legend からは、非 null な参照が保証される
 * `marginReportSeriesByKey.deposits` のような形で個別参照するか、表示順が必要な場合は
 * 派生配列 {@link marginReportSeries} を使う。
 */
export const marginReportSeriesByKey = {
  totalCreditAmount: {
    key: 'totalCreditAmount',
    labelKey: `${LEGEND_KEY}.total_credit_amount`,
    descriptionKey: `${LEGEND_DESCRIPTION_KEY}.total_credit_amount`,
    hybridOnly: true,
    control: 'checkbox',
    drawOrder: 5,
    style: {
      stroke: '#006466',
      strokeWidth: 2,
      fill: NONE_FILL,
      dot: 'none',
    },
    defaultVisible: { hybrid: true, depositOnly: false },
  },
  deposits: {
    key: 'deposits',
    labelKey: `${LEGEND_KEY}.deposits`,
    descriptionKey: `${LEGEND_DESCRIPTION_KEY}.deposits`,
    hybridOnly: false,
    control: 'checkbox',
    drawOrder: 4,
    style: {
      hybrid: {
        stroke: '#006466',
        strokeDasharray: '6 3',
        strokeWidth: 2,
        fill: { kind: 'solid', color: 'rgba(0,100,102,0.10)', opacity: 0.6 },
        dot: 'circle',
        legendSwatch: 'band',
      },
      depositOnly: {
        stroke: '#006466',
        strokeWidth: 2,
        fill: NONE_FILL,
        dot: 'circle',
      },
    },
    defaultVisible: { hybrid: false, depositOnly: true },
  },
  requiredMargin: {
    key: 'requiredMargin',
    labelKey: `${LEGEND_KEY}.required_margin`,
    descriptionKey: `${LEGEND_DESCRIPTION_KEY}.required_margin`,
    hybridOnly: false,
    control: 'checkbox',
    drawOrder: 3,
    style: {
      stroke: '#D80004',
      strokeDasharray: '8 4',
      strokeWidth: 2,
      // opacity 未指定 = recharts の Area 既定値（0.6）を使う意図
      fill: { kind: 'solid', color: 'rgba(216,0,4,0.15)' },
      dot: 'square',
    },
    defaultVisible: { hybrid: true, depositOnly: true },
  },
  initialMargin: {
    key: 'initialMargin',
    labelKey: `${LEGEND_KEY}.initial_margin`,
    descriptionKey: `${LEGEND_DESCRIPTION_KEY}.initial_margin`,
    hybridOnly: false,
    control: 'checkbox',
    drawOrder: 6,
    style: {
      stroke: '#EA580C',
      strokeDasharray: '16 4',
      strokeWidth: 2,
      fill: NONE_FILL,
      dot: 'hexagon',
    },
    defaultVisible: { hybrid: false, depositOnly: true },
  },
  unrealizedAmount: {
    key: 'unrealizedAmount',
    labelKey: `${LEGEND_KEY}.unrealized_amount`,
    descriptionKey: `${LEGEND_DESCRIPTION_KEY}.unrealized_amount`,
    hybridOnly: false,
    control: 'checkbox',
    drawOrder: 1,
    style: {
      stroke: '#4164E0',
      strokeDasharray: '2 4',
      strokeWidth: 2,
      fill: GRADIENT_FILL,
      dot: 'triangle',
    },
    defaultVisible: { hybrid: false, depositOnly: false },
  },
  realizedAmount: {
    key: 'realizedAmount',
    labelKey: `${LEGEND_KEY}.realized_amount`,
    descriptionKey: `${LEGEND_DESCRIPTION_KEY}.realized_amount`,
    hybridOnly: false,
    control: 'checkbox',
    drawOrder: 2,
    style: {
      stroke: '#8B5CF6',
      strokeDasharray: '12 4 2 4',
      strokeWidth: 2,
      fill: GRADIENT_FILL,
      dot: 'diamond',
    },
    defaultVisible: { hybrid: false, depositOnly: false },
  },
  materializedUnsettledAmount: {
    key: 'materializedUnsettledAmount',
    labelKey: `${LEGEND_KEY}.mtm`,
    hybridOnly: false,
    control: 'checkbox',
    drawOrder: 0,
    style: {
      stroke: '#A78B00',
      strokeDasharray: '4 4',
      strokeWidth: 2,
      fill: GRADIENT_FILL,
      dot: 'none',
    },
    defaultVisible: { hybrid: false, depositOnly: true },
  },
  maxTradableAmount: {
    key: 'maxTradableAmount',
    labelKey: `${LEGEND_KEY}.max_tradable_amount`,
    descriptionKey: `${LEGEND_DESCRIPTION_KEY}.max_tradable_amount`,
    hybridOnly: true,
    control: 'checkbox',
    drawOrder: 7,
    style: {
      stroke: '#565656',
      strokeDasharray: '3 3',
      strokeWidth: 1.5,
      fill: NONE_FILL,
      dot: 'none',
    },
    defaultVisible: { hybrid: true, depositOnly: false },
  },
  marginCallTiming: {
    key: 'marginCallTiming',
    labelKey: `${LEGEND_KEY}.margin_call_timing`,
    descriptionKey: `${LEGEND_DESCRIPTION_KEY}.margin_call_timing`,
    hybridOnly: false,
    control: 'switch',
    // marginCallTiming は drawableSeries から除外される（ReferenceLine として別扱い）ため、
    // この値自体は描画順に影響しない。型上の網羅性のために便宜上の値を置く。
    drawOrder: 8,
    style: {
      stroke: '#EA740C',
      strokeWidth: 3,
      fill: NONE_FILL,
      dot: 'none',
    },
    defaultVisible: { hybrid: true, depositOnly: true },
  },
} as const satisfies Record<MarginReportSeriesKey, MarginReportSeriesDef>

/**
 * 凡例・グラフ描画順の基準となるキー順。
 * {@link marginReportSeriesByKey} が正本で、これはあくまで表示順の指定。
 * ここに列挙されなかったキーは {@link marginReportSeries} に現れない点に注意
 * （{@link marginReportSeriesByKey} 自体の 9 キー網羅はコンパイル時に保証されている）。
 */
const MARGIN_REPORT_SERIES_KEY_ORDER = [
  'totalCreditAmount',
  'deposits',
  'requiredMargin',
  'initialMargin',
  'unrealizedAmount',
  'realizedAmount',
  'materializedUnsettledAmount',
  'maxTradableAmount',
  'marginCallTiming',
] as const satisfies readonly MarginReportSeriesKey[]

/**
 * 表示順（凡例・グラフ描画順）に並んだ系列定義の配列。{@link marginReportSeriesByKey} から導出。
 *
 * 明示的な型注釈を付けない。`: readonly MarginReportSeriesDef[]` のような注釈を付けると
 * `MarginReportSeriesDef.labelKey: string`（widen された宣言）に揃えられてしまい、各要素が
 * 個別に持っている literal な labelKey / descriptionKey の型が失われる（portal 等の
 * `t(def.labelKey)` が型エラーになる）。注釈を外して推論に任せることで、各要素の型が
 * {@link marginReportSeriesByKey} の該当エントリの型（literal 型を保持）のユニオンのまま伝播する。
 */
export const marginReportSeries = MARGIN_REPORT_SERIES_KEY_ORDER.map(
  (key) => marginReportSeriesByKey[key],
)

/**
 * isHybrid（ハイブリッドスキーム=履行保証あり）に応じた初期表示 ON/OFF の
 * Record を作る。
 *
 * 注意: isHybrid はクエリ完了後に false → true へ遷移しうる値（初期値は false
 * 固定で、データ取得が終わってはじめて確定する）。そのため `useState(() =>
 * createMarginReportVisibility(isHybrid))` のように useState の初期値として
 * 一度使うだけでは不十分で、isHybrid が変化した際にはこの関数で作り直して
 * setState する必要がある。React 公式の "Adjusting state when a prop changes"
 * パターン（render 中に前回値との差分を見て setState する）で呼び出すこと。
 * useEffect で追従すると 1 フレーム分古い state で描画されるちらつきが起きるため
 * 使わない（呼び出し側 MarginReportChart.tsx 参照）。
 */
export function createMarginReportVisibility(
  isHybrid: boolean,
): Record<MarginReportSeriesKey, boolean> {
  return MARGIN_REPORT_SERIES_KEY_ORDER.reduce<
    Record<MarginReportSeriesKey, boolean>
  >(
    (acc, key) => {
      const def = marginReportSeriesByKey[key]
      // hybridOnly な系列は depositOnly モードでは無条件で非表示にする（規約ではなくコードで保証）。
      acc[key] = !isSeriesAvailable(def, isHybrid)
        ? false
        : isHybrid
          ? def.defaultVisible.hybrid
          : def.defaultVisible.depositOnly
      return acc
    },
    {} as Record<MarginReportSeriesKey, boolean>,
  )
}
